import React, { useState } from 'react';
import { Box, Button, SvgIcon, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import getMuiIcon from '../../../utils/getMuiIcon';
import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import useAction from '../../../utils/useAction';
import {
  createCollection as createCollectionAction
} from '../../../actions/collections.actions';

interface CreateNewCollectionProps {
  onSetNewCollectionId: (collectionId: string) => void;
}

interface ICreateCollectionResponse {
  id: string,
}

const CreateNewCollection: React.FC<CreateNewCollectionProps> = ({ onSetNewCollectionId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const { t } = useTranslation();
  const createCollection = useAction(createCollectionAction);

  const handleAddNewCollection = () => {
    setIsEditing(false);
    createCollection({ title: newCollectionName })
      .then((data: ICreateCollectionResponse) => {
        setNewCollectionName('');
        onSetNewCollectionId(data.id);
      });
  };

  const CreateNewCollectionButton = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: spaces.md,
    }}>
      <Button
        data-testid='create-new-collection-button'
        variant='outlined'
        color='primary'
        size='large'
        startIcon={
          <SvgIcon component={getMuiIcon('addCollection')} viewBox='0 0 20 20' />
        }
        sx={{
          width: '100%',
        }}
        onClick={() => setIsEditing(true)}
      >
        {t('Create new collection')}
      </Button>
    </Box>
  );

  const EditCollectionForm = (
    <Box 
      data-testid='create-new-collection-form'
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: spaces.md,
      }}
    >
      <TextField
        variant='outlined'
        size='small'
        data-testid='create-new-collection-input-field'
        value={newCollectionName}
        onChange={({ target }) => setNewCollectionName(target.value)}
        sx={{
          flex: 1,
          borderRadius: borderRadius.sm,
          '& .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
            borderRadius: borderRadius.xs,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
          },
          border: 'none',
        }}
        placeholder={t('e.g. justifying opinions, 5th grade')}
      />
      <Button
        data-testid='create-new-collection-add-button'
        variant='contained'
        color='primary'
        size='medium'
        sx={{
          paddingX: spaces.md,
          paddingY: spaces.xxs,
        }}
        startIcon={
          <SvgIcon 
            component={getMuiIcon('add')} 
            sx={{ stroke: palette.primary.contrastText }}
          />
        }
        onClick={handleAddNewCollection}
      >
        {t('Add')}
      </Button>
    </Box>
  );

  return isEditing ? EditCollectionForm : CreateNewCollectionButton;
};

export default CreateNewCollection;
